import axios from "axios";

// Stops a double click (or a double Ctrl+S, or a flaky-touchscreen double tap)
// from sending the same create / save / delete / approve request twice, in one
// place for every screen instead of a guard in each button handler.
//
// A write is a "duplicate" when its method, URL and logical body match one that
// is still in flight or finished less than COOLDOWN_MS ago. The duplicate is
// dropped without reaching the server - the first call's result is what the
// screen reacts to, exactly as if the second click never happened. The cooldown
// matters: on a fast server the first request can finish before a slow second
// click lands, which an in-flight-only check would let through.
//
// Not guarded: reads (GET), and the POSTs in EXEMPT below - read-only lookups
// and rapid incremental actions where the same body twice is genuinely meant
// (tapping "add" twice on a table order adds two).
//
// subscribeBusy() reports how many guarded writes are running so the app can
// show a "Working..." lock (components/Loader/ActionLockOverlay.js).

const COOLDOWN_MS = 700;

const EXEMPT = [
  /\/stocks\/quantities$/,
  /\/stocks\/allocate$/,
  /\/invoices\/client$/,
  /\/meetings\/client$/,
  /\/units\/convert$/,
  /\/order-sessions\/[^/]+\/(add-item|update-item|remove-item)$/,
  /\/program-logs\/events$/,
  /\/users\/refresh$/,
  /\/generate(-invoice)?-pdf$/,
];

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const VOLATILE_KEYS = new Set(["createdAt", "updatedAt", "timestamp", "enteredAt"]);

// Same click twice builds the same body except for "now" timestamps, so those
// are ignored when comparing.
const stableBody = (data) => {
  if (data === undefined || data === null) return "";
  if (typeof data === "string") return data.replace(/"\d{4}-\d{2}-\d{2}T[\d:.]+Z?"/g, '"<dt>"');
  try {
    return JSON.stringify(data, (key, value) => {
      if (VOLATILE_KEYS.has(key)) return undefined;
      if (typeof value === "string" && ISO_DATETIME.test(value)) return "<dt>";
      return value;
    });
  } catch (error) {
    return null; // not serialisable (circular, ...) - don't guard it
  }
};

const entries = new Map();
let busy = 0;
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener(busy));

export const subscribeBusy = (listener) => {
  listeners.add(listener);
  listener(busy);
  return () => listeners.delete(listener);
};

export const attachMutationGuard = (instance) => {
  instance.interceptors.request.use((config) => {
    const method = String(config.method || "get").toLowerCase();
    if (method === "get" || method === "head" || method === "options") return config;
    if (config.allowRepeat === true) return config;
    if (typeof FormData !== "undefined" && config.data instanceof FormData) return config;

    const path = String(config.url || "").split("?")[0];
    if (EXEMPT.some((pattern) => pattern.test(path))) return config;

    const body = stableBody(config.data);
    if (body === null) return config;
    const key = `${method} ${path} ${body}`;

    const original = config.adapter || instance.defaults.adapter || axios.defaults.adapter;
    config.adapter = (cfg) => {
      const existing = entries.get(key);
      if (existing && (!existing.finished || Date.now() < existing.until)) {
        // Duplicate: swallow it. It never settles, so nothing downstream runs
        // twice; release the top progress bar's count for it by hand since its
        // response interceptor will never fire.
        if (typeof cfg.__endLoading === "function") cfg.__endLoading();
        // eslint-disable-next-line no-console
        console.debug("[mutationGuard] duplicate request ignored:", method.toUpperCase(), path);
        return new Promise(() => {});
      }

      const entry = { finished: false, until: 0 };
      entries.set(key, entry);
      busy += 1;
      emit();

      const finish = () => {
        entry.finished = true;
        entry.until = Date.now() + COOLDOWN_MS;
        busy = Math.max(0, busy - 1);
        emit();
        setTimeout(() => {
          if (entries.get(key) === entry) entries.delete(key);
        }, COOLDOWN_MS + 50);
      };

      return axios.getAdapter(original)(cfg).then(
        (response) => {
          finish();
          return response;
        },
        (error) => {
          finish();
          throw error;
        }
      );
    };
    return config;
  });
};
